/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { ClinicProfileSettingsResponse } from "../../../../models/clinic/profile/ClinicProfileSettingsResponse";
import { useForm } from "react-hook-form";
import { BasicProfileSettingsRequest } from "../../../../models/clinic/profile/BasicProfileSettingsRequest";
import { strings } from "../../../../common/Strings/Strings";
import ClinicProfileApi from "../../../../api/ClinicProfileApi";
import { canManageClinic, getGeneralError } from "../../../../util/helperFunctions";
import Select from "../../../../components/ReactHookFormFields/General/Select/Select";
import { ClinicProfileVisibility } from "../../../../models/clinic/profile/ClinicProfileVisibility";
import Textarea from "../../../../components/ReactHookFormFields/General/Textarea";
import { clinicProfileVisibilityOptions } from "../ClinicProfileSettings";
import AlertBox, { AlertType } from "../../../../components/AlertBox";
import Button from "../../../../components/Button";
import { ClinicResponse } from "../../../../models/clinic/ClinicResponse";

interface Props {
  clinic: ClinicResponse;
  settings: ClinicProfileSettingsResponse;
  setSettings: (settings: ClinicProfileSettingsResponse) => void;
}

const BasicSettings: React.FC<Props> = (props: Props) => {
  const { clinic, settings, setSettings } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<BasicProfileSettingsRequest>({
    mode: "onSubmit",
    defaultValues: settings,
  });

  useEffect(() => {
    reset(settings);
  }, [settings]);

  const saveSettings = async (request: BasicProfileSettingsRequest) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await ClinicProfileApi.updateBasicProfileSettings(clinic.id, request);
      setSettings(response.data);
      setSuccess(true);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(saveSettings)}>
      <div className="tw-card divide-y divide-gray-200 dark:divide-gray-700">
        <div className="p-4 flex items-center">
          <p className="uppercase">{strings.basicInfo}</p>
        </div>
        <div className="p-4 space-y-4">
          <Select
            label={strings.clinicProfileVisibility}
            name="profileVisibility"
            register={register}
            options={clinicProfileVisibilityOptions.filter((i) => i.value != ClinicProfileVisibility.PRIVATE)}
            error={errors.profileVisibility}
            readOnly={!canManageClinic(clinic)}
          />
          <Textarea
            label={strings.clinicProfileIntroduction}
            name="introduction"
            register={register}
            minRows={5}
            error={errors.introduction}
            maxLength={5000}
            readOnly={!canManageClinic(clinic)}
          />
          <AlertBox message={error} />
          <AlertBox closeAble={false} hidden={!success} message={strings.savedSuccessfully} type={AlertType.SUCCESS} />
          <div className="text-right">
            <Button
              disabled={loading || !isDirty}
              loading={loading}
              type="submit"
              fullWidth={false}
              hidden={!canManageClinic(clinic)}
            >
              {strings.save}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default BasicSettings;
